import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CheckboxGroupInput, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { depositWithdrawalMethodsChoices } from '@/components/global/options/selectOptions.jsx';
import { settingsCheckboxGroupStyles, settingsSectionStyles } from '@/themes/commonStyles/settingsSection.js';

const StyledSettingsSection = styled(Box)(settingsSectionStyles);
const StyledCheckboxGroup = styled(Box)(settingsCheckboxGroupStyles);

const DISCOUNT_LEVELS = [1, 2, 3, 4, 5, 6];

export const SettingsSection = ({ title, children }) => (
    <StyledSettingsSection>
        {title ? (
            <Typography variant='h5' gutterBottom>
                {title}
            </Typography>
        ) : null}
        {children}
    </StyledSettingsSection>
);

SettingsSection.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node,
};

export const SettingsCheckboxGroup = ({ children }) => <StyledCheckboxGroup>{children}</StyledCheckboxGroup>;

SettingsCheckboxGroup.propTypes = {
    children: PropTypes.node,
};

const choicesWithoutIcon = choices =>
    choices.map(choice => {
        const choiceWithoutIcon = { ...choice };
        delete choiceWithoutIcon.icon;
        return choiceWithoutIcon;
    });

export const DepositWithdrawalMethodsField = ({ label, validate, defaultValue }) => {
    const translate = useTranslate();
    const { watch, setValue } = useFormContext();
    const selectedMethods = watch('depositWithdrawalMethods');

    useEffect(() => {
        const currentValues = selectedMethods || [];
        if (!currentValues.includes('cash')) {
            setValue('depositWithdrawalMethods', [...currentValues, 'cash']);
        }
    }, [selectedMethods, setValue]);

    const choices = choicesWithoutIcon(depositWithdrawalMethodsChoices(translate)).map(choice =>
        choice.id === 'cash' ? { ...choice, disabled: true } : choice,
    );

    return (
        <SettingsCheckboxGroup>
            <CheckboxGroupInput source='depositWithdrawalMethods' label={label} choices={choices} validate={validate} defaultValue={defaultValue} row={false} />
        </SettingsCheckboxGroup>
    );
};

DepositWithdrawalMethodsField.propTypes = {
    label: PropTypes.string.isRequired,
    validate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    defaultValue: PropTypes.arrayOf(PropTypes.string),
};

const DiscountRow = ({ title, sourcePrefix, labelKey, InputField, validate, inputProps }) => {
    const translate = useTranslate();

    return (
        <>
            <Typography variant='h6' gutterBottom>
                {title}
            </Typography>
            <Box className='settings-discount-row'>
                {DISCOUNT_LEVELS.map(level => (
                    <Box key={`${sourcePrefix}${level}`}>
                        <InputField
                            source={`${sourcePrefix}${level}`}
                            label={translate(labelKey, { limit: level, value: level })}
                            fullWidth
                            validate={validate}
                            {...inputProps}
                        />
                    </Box>
                ))}
            </Box>
        </>
    );
};

DiscountRow.propTypes = {
    title: PropTypes.string.isRequired,
    sourcePrefix: PropTypes.string.isRequired,
    labelKey: PropTypes.string.isRequired,
    InputField: PropTypes.elementType.isRequired,
    validate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    inputProps: PropTypes.object,
};

export const SettingsDiscountRanges = ({ InputField, limitTitle, valueTitle, limitLabelKey, valueLabelKey, validate, inputProps = {} }) => (
    <>
        <DiscountRow
            title={limitTitle}
            sourcePrefix='discountLimitL'
            labelKey={limitLabelKey}
            InputField={InputField}
            validate={validate}
            inputProps={inputProps}
        />
        <DiscountRow
            title={valueTitle}
            sourcePrefix='discountValueL'
            labelKey={valueLabelKey}
            InputField={InputField}
            validate={validate}
            inputProps={inputProps}
        />
    </>
);

SettingsDiscountRanges.propTypes = {
    InputField: PropTypes.elementType.isRequired,
    limitTitle: PropTypes.string.isRequired,
    valueTitle: PropTypes.string.isRequired,
    limitLabelKey: PropTypes.string.isRequired,
    valueLabelKey: PropTypes.string.isRequired,
    validate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    inputProps: PropTypes.object,
};
